import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faBinoculars, faGolfBallTee } from '@fortawesome/free-solid-svg-icons';
import Offcanvas from 'react-bootstrap/Offcanvas';
import '../css/Sidebar.css';
import {
  getTotalThisWeek,
  getTotalNextWeek,
  getTotalWeekAfterNext,
  filterBillsByCurrentWeek,
} from '../services/BillService';
import DateService from '../services/DateService';
import { getPays, getPaysWithUnpaidTransfers } from '../services/PayService';
import { getBills } from '../services/BillService';
import { getBalance } from '../services/BankAccountService';
import { getNextThreeWeeksDates, formatShortDateRange, formatDate } from '../utils/dateUtils';
import { getBankAccounts } from '../services/BankAccountService';

function Sidebar({ show, handleClose, currentBalance }) {
  const [balance, setBalance] = useState(0);
  const [billsData, setBillsData] = useState([]);
  const [paysData, setPaysData] = useState([]);
  const [totalThisWeek, setTotalThisWeek] = useState(0);
  const [totalNextWeek, setTotalNextWeek] = useState(0);
  const [totalWeekAfterNext, setTotalWeekAfterNext] = useState(0);
  const [nextThreeWeeksData, setNextThreeWeeksData] = useState([]);
  const [trackedBankAccountId, setTrackedBankAccountId] = useState(null);
  // to match other values for calculations
  const currentBalanceNum = parseFloat(currentBalance);

  // Simple cache to avoid recomputing next-three-weeks rapidly
  const weeksCacheRef = useRef({ at: 0, data: null, bankAccountId: null });
  const WEEKS_TTL_MS = 60_000; // 60 seconds

  let thisWeekDiff = 0;
  let nextWeekDiff = 0;
  let weekAfterNextDiff = 0;
  let nextWeekTotalInBank = 0;
  let weekAfterNextTotalInBank = 0;
  let nextWeekMin = 0;
  let weekAfterNextMin = 0;
  let thisWeekTotalInBank = 0;
  let thisWeekOwing = 0;
  let nextWeekOwing = 0;

  useEffect(() => {
    // Only fetch when the sidebar is actually visible
    if (!show) return;

    const fetchData = async () => {
      try {
        const { getTrackingAccountBalance } = await import('../services/BankAccountService');
        const balanceData = await getTrackingAccountBalance();
        setBalance(balanceData.amount);

        // Load supporting lists lazily only when open
        const bills = await getBills();
        setBillsData(bills);

        const pays = await getPays();
        setPaysData(pays);

        // Tracked account for subsequent calculations
        try {
          const bankAccounts = await getBankAccounts();
          const trackedAccount = bankAccounts.find(account => account.include_in_tracking === true);
          if (trackedAccount) {
            setTrackedBankAccountId(trackedAccount.id);
          }
        } catch (error) {
          console.error('Error fetching tracked bank account:', error.name);
        }
      } catch (error) {
        console.error('Error fetching sidebar data:', error.name);
      }
    };

    fetchData();
  }, [show]);

  // When tracked bank account is known and sidebar is open, compute weekly panels
  useEffect(() => {
    if (!show || trackedBankAccountId === null) return;

    const fetchWeeks = async () => {
      const now = Date.now();
      if (
        weeksCacheRef.current.data &&
        weeksCacheRef.current.bankAccountId === trackedBankAccountId &&
        (now - weeksCacheRef.current.at) < WEEKS_TTL_MS
      ) {
        setNextThreeWeeksData(weeksCacheRef.current.data);
        return;
      }

      const data = await computeNextThreeWeeksData(trackedBankAccountId);
      weeksCacheRef.current = { at: Date.now(), data, bankAccountId: trackedBankAccountId };
      setNextThreeWeeksData(data);
    };

    fetchWeeks();
  }, [show, trackedBankAccountId]);

  // Totals for headers – only when open
  useEffect(() => {
    if (!show) return;

    getTotalThisWeek()
      .then((total) => setTotalThisWeek(parseFloat(total)))
      .catch((error) => console.error('Error fetching total this week:', error));

    getTotalNextWeek()
      .then((total) => setTotalNextWeek(parseFloat(total)))
      .catch((error) => console.error('Error fetching total next week:', error));

    getTotalWeekAfterNext()
      .then((total) => setTotalWeekAfterNext(parseFloat(total)))
      .catch((error) => console.error('Error fetching total week after next:', error));
  }, [show]);

  const computeNextThreeWeeksData = async (trackedBankAccountId) => {
    const fortnightStartDate = await DateService.getFortnightStartDate();
    const startDateStr = fortnightStartDate.fortnights[0].start_date; // Get the start date string
    const startDate = new Date(startDateStr); // Convert to Date object

    const today = new Date();
    const daysSinceFortnightStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    const currentWeekNumber = Math.floor(daysSinceFortnightStart / 7) % 2 + 1; // Determine if it's week 1 or 2

    const weekStartDates = [0, 7, 14].map((offset) => {
      const newStartDate = new Date(startDate);
      if (currentWeekNumber === 1) {
        newStartDate.setDate(startDate.getDate() + offset);
      } else {
        newStartDate.setDate(startDate.getDate() + 7 + offset);
      }
      return newStartDate;
    });

    return Promise.all(
      weekStartDates.map(async (startDate, index) => {
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        const nextStartDate = new Date(endDate);
        nextStartDate.setDate(endDate.getDate() + 1);

        let totalAmountOwing;
        const { totalUnpaidTransferAmount } = await getPaysWithUnpaidTransfers(startDate, nextStartDate, trackedBankAccountId);

        if (index === 0) {
          totalAmountOwing = await getTotalThisWeek();
        } else if (index === 1) {
          totalAmountOwing = await getTotalNextWeek();
        } else {
          totalAmountOwing = await getTotalWeekAfterNext();
        }

        const formattedDateRange = formatShortDateRange(startDate, endDate);

        return {
          index,
          date: formattedDateRange,
          totalAmountOwing: parseFloat(totalAmountOwing) || 0,
          totalIncomingPays: parseFloat(totalUnpaidTransferAmount) || 0,
        };
      })
    );
  };

  const renderBoxContent = (index, weekData) => {
    // Helper function to create props for each div
    const getCommonProps = (key) => ({
      className: `box-${index + 1}`,
//      key: `${key}-${index}`,
    });

    switch (index) {
      case 0:
        thisWeekOwing = parseInt(weekData.totalAmountOwing);
//        thisWeekTotalInBank = parseInt(currentBalanceNum);
        thisWeekTotalInBank = balance;
        thisWeekDiff = parseInt(balance + weekData.totalIncomingPays - weekData.totalAmountOwing);
        return (
          <>
          <div key={`outgoing-${index}`} {...getCommonProps()}>
            <span>Outgoing</span><br />
            <span>${thisWeekOwing}</span>
          </div>
          <div key={`incoming-${index}`} {...getCommonProps()}>
            <span>Incoming</span><br />
            <span>${weekData.totalIncomingPays}</span>
          </div>
          <div key={`total-${index}`} {...getCommonProps()}>
            <span>End Balance</span><br />
            <span>${thisWeekDiff}</span>
          </div>
          </>
        );
      case 1:
        nextWeekOwing = parseInt(weekData.totalAmountOwing);
        nextWeekTotalInBank = parseInt(balance + weekData.totalIncomingPays);
        nextWeekDiff = parseInt(nextWeekTotalInBank - nextWeekOwing);

        return (
          <>
          <div key={`outgoing-${index}`} {...getCommonProps()}>
            <span>Outgoing</span><br />
            <span>${nextWeekOwing}</span>
          </div>
          <div key={`incoming-${index}`} {...getCommonProps()}>
            <span>Incoming</span><br />
            <span>${weekData.totalIncomingPays}</span>
          </div>
          <div key={`total-${index}`} {...getCommonProps()}>
            <span>End Balance</span><br />
            <span>${nextWeekDiff}</span>
          </div>
          </>
        );
      case 2:
        weekAfterNextTotalInBank = parseInt(balance + weekData.totalIncomingPays);
        weekAfterNextDiff = parseInt(weekAfterNextTotalInBank - weekData.totalAmountOwing);

        return (
          <>
          <div key={`outgoing-${index}`} {...getCommonProps()}>
            <span>Outgoing</span><br />
            <span>${weekAfterNextDiff + weekData.totalAmountOwing}</span>
          </div>
          <div key={`incoming-${index}`} {...getCommonProps()}>
            <span>Incoming</span><br />
            <span>${weekData.totalIncomingPays}</span>
          </div>
          <div key={`total-${index}`} {...getCommonProps()}>
            <span>End Balance</span><br />
            <span>${weekAfterNextDiff}</span>
          </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        backdrop={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Next Three Weeks</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {nextThreeWeeksData.map((weekData) => (
            <div key={weekData.index} className="week-container">
              <h4 style={{
                fontSize: "24px",
                fontWeight: "bold",
                borderBottom: "2px solid #A0C2BB",
                display: "inline-block",
                paddingBottom: "10px"
              }}>
                {weekData.index === 0 ? (
                  <>
                    <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "10px" }} />
                    Current Week
                  </>
                ) : weekData.index === 1 ? (
                  <>
                    <FontAwesomeIcon icon={faBinoculars} style={{ marginRight: "10px" }} />
                    Next Week
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faGolfBallTee} style={{ marginRight: "10px" }} />
                    Week After Next
                  </>
                )}
              </h4>
              <h6>{weekData.date}</h6>
              <div className="info-box">
                {renderBoxContent(weekData.index, weekData)}
              </div>
            </div>
          ))}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default Sidebar;
